import { Search } from "lucide-react";
import { useDeferredValue, useState } from "react";

import type { UsageStatsResponse } from "@/lib/types";

import { DailyBarChart } from "./DailyBarChart";
import { DailyStackedChart } from "./DailyStackedChart";
import { formatDuration, formatInt, formatTokens, TOKEN_TYPE_COLORS } from "./formatters";
import { PricingComparison } from "./PricingComparison";
import { ToolUsageTable } from "./ToolUsageTable";

export type StatsDetailSection = "tokens" | "tools" | "capabilities" | "cost";

function StatCard({
  label,
  value,
  colorClass = "text-cyber-text",
}: {
  label: string;
  value: string;
  colorClass?: string;
}) {
  return (
    <div className="border-cyber-border bg-cyber-surface/30 rounded-lg border px-3 py-2">
      <div className="text-cyber-muted/70 text-[10px] tracking-wide uppercase">{label}</div>
      <div className={`mt-1 text-sm ${colorClass}`}>{value}</div>
    </div>
  );
}

function CapabilityUsageTable({ data }: { data: UsageStatsResponse["capabilityUsage"] }) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search).trim().toLowerCase();
  const rows = deferredSearch
    ? data.filter((row) => row.capabilityId.toLowerCase().includes(deferredSearch))
    : data;

  if (data.length === 0) {
    return <p className="text-cyber-muted/60 text-xs">No capability usage available.</p>;
  }

  return (
    <>
      <div className="relative mb-3">
        <Search
          size={13}
          className="text-cyber-muted pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search capabilities"
          aria-label="Search capability usage"
          className="border-cyber-border bg-cyber-bg/50 text-cyber-text placeholder:text-cyber-muted/60 focus:border-cyber-cyan/60 w-full rounded border py-2 pr-3 pl-8 text-xs transition-colors outline-none"
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-cyber-muted/60 py-6 text-center text-xs">
          No capabilities match this search.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-cyber-border/50 text-cyber-muted/70 border-b text-left text-[10px] tracking-wide uppercase">
                <th className="pr-4 pb-2 font-medium">Capability</th>
                <th className="pr-4 pb-2 text-right font-medium">Calls</th>
                <th className="pr-4 pb-2 text-right font-medium">Failed</th>
                <th className="pr-4 pb-2 text-right font-medium">Timeouts</th>
                <th className="pr-4 pb-2 text-right font-medium">Rate limited</th>
                <th className="pr-4 pb-2 text-right font-medium">Denied</th>
                <th className="pb-2 text-right font-medium">Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.capabilityId} className="border-cyber-border/20 border-b">
                  <td className="text-cyber-text py-1.5 pr-4 font-mono">{row.capabilityId}</td>
                  <td className="text-cyber-muted py-1.5 pr-4 text-right">
                    {formatInt(row.calls)}
                  </td>
                  <td className="text-cyber-muted py-1.5 pr-4 text-right">
                    {formatInt(row.failed)}
                  </td>
                  <td className="text-cyber-muted py-1.5 pr-4 text-right">
                    {formatInt(row.timeouts)}
                  </td>
                  <td className="text-cyber-muted py-1.5 pr-4 text-right">
                    {formatInt(row.rateLimited)}
                  </td>
                  <td className="text-cyber-muted py-1.5 pr-4 text-right">
                    {formatInt(row.denied)}
                  </td>
                  <td className="text-cyber-muted py-1.5 text-right">
                    {formatDuration(row.avgDurationMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-cyber-muted/60 mt-3 text-xs">
        Timeouts and rate limits are subsets of Failed. Rate limits include recorded concurrency
        rejections and provider failures, not API-key hourly limits. Older unrecorded rejections are
        unavailable. Average duration excludes admission rejections.
      </p>
    </>
  );
}

export function StatsAdvanced({
  data,
  section,
}: {
  data: UsageStatsResponse;
  section: StatsDetailSection;
}) {
  const { totals, byDay, toolUsage, capabilityUsage } = data;

  const cacheDenominator = totals.cacheReadTokens + totals.cacheWriteTokens + totals.inputTokens;
  const cacheEfficiency =
    cacheDenominator === 0 ? 0 : Math.round((totals.cacheReadTokens / cacheDenominator) * 100);

  const agenticDepthData = byDay.map((row) => ({
    day: row.day,
    responses: row.responses,
    value: row.avgStepCount,
  }));

  const latencyData = byDay
    .filter((row) => row.avgLatencyMs > 0)
    .map((row) => ({
      day: row.day,
      responses: row.responses,
      value: row.avgLatencyMs,
    }));

  if (section === "tools") {
    return (
      <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
        <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
          Tool usage ({formatInt(totals.totalToolCalls)} total calls)
        </h3>
        <ToolUsageTable data={toolUsage} />
      </section>
    );
  }

  if (section === "capabilities") {
    return (
      <>
        <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
          <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
            Capability executions (
            {formatInt(capabilityUsage.reduce((total, row) => total + row.calls, 0))} total)
          </h3>
          <CapabilityUsageTable data={capabilityUsage} />
        </section>
      </>
    );
  }

  if (section === "cost") {
    return (
      <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
        <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
          Estimated API cost (if pay-per-token)
        </h3>
        <p className="text-cyber-muted mb-3 text-xs">
          Provider-reported cost can be missing, including zero-valued OpenAI records. These
          estimates are not reconciled billing and omit untracked child and service costs.
        </p>
        <PricingComparison byModel={data.byModel} />
      </section>
    );
  }

  return (
    <>
      {/* Token breakdown cards */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
        <StatCard label="Total" value={formatTokens(totals.totalTokens)} />
        <StatCard
          label="Output"
          value={formatTokens(totals.outputTokens)}
          colorClass={TOKEN_TYPE_COLORS.output.label}
        />
        <StatCard
          label="Cache reads"
          value={formatTokens(totals.cacheReadTokens)}
          colorClass={TOKEN_TYPE_COLORS.cacheRead.label}
        />
        <StatCard
          label="Cache writes"
          value={formatTokens(totals.cacheWriteTokens)}
          colorClass={TOKEN_TYPE_COLORS.cacheWrite.label}
        />
        <StatCard
          label="Base input"
          value={formatTokens(totals.inputTokens)}
          colorClass={TOKEN_TYPE_COLORS.input.label}
        />
        <StatCard
          label="Reasoning"
          value={formatTokens(totals.reasoningTokens)}
          colorClass={TOKEN_TYPE_COLORS.reasoning.label}
        />
      </div>

      {/* Summary row: cache efficiency + tool stats + latency */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <StatCard label="Cache efficiency" value={`${cacheEfficiency}%`} />
        <StatCard label="Avg steps / prompt" value={String(totals.avgStepCount)} />
        <StatCard
          label="Avg latency"
          value={totals.avgLatencyMs > 0 ? formatDuration(totals.avgLatencyMs) : "N/A"}
        />
        <StatCard
          label="Tool error rate"
          value={
            totals.totalToolCalls > 0
              ? `${((totals.totalToolErrors / totals.totalToolCalls) * 100).toFixed(1)}%`
              : "N/A"
          }
        />
      </div>

      {/* Daily token breakdown stacked chart */}
      <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
        <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
          Daily token breakdown
        </h3>
        <DailyStackedChart data={byDay} />
      </section>

      {/* Two-column: Agentic Depth + Latency */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Agentic depth chart */}
        <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
          <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
            Agentic depth (avg steps / prompt)
          </h3>
          <DailyBarChart
            data={agenticDepthData}
            color="rgb(var(--cyber-magenta))"
            valueLabel="steps"
            formatValue={(v) => v.toFixed(1)}
          />
        </section>

        {/* Response latency chart */}
        <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
          <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
            Avg response latency
          </h3>
          {latencyData.length > 0 ? (
            <DailyBarChart
              data={latencyData}
              color="rgb(var(--cyber-green))"
              valueLabel=""
              formatValue={formatDuration}
            />
          ) : (
            <p className="text-cyber-muted/60 py-8 text-center text-xs">
              No latency data available.
            </p>
          )}
        </section>
      </div>
    </>
  );
}
